import React, { useRef, useEffect, useState } from 'react';
import { Modal } from './modal';
import './drawing-canvas.css';

interface DrawingCanvasProps {
  onSave: (dataUrl: string) => void;
  initialDrawing?: string;
  // While saving, the canvas is locked and covered by a loading overlay.
  isSaving?: boolean;
  savingMessage?: string;
}

// The drawing is exported at a fixed poster-shaped (2:3) resolution regardless
// of the on-screen size, so image models receive a large, opaque sketch whose
// layout maps 1:1 onto a portrait movie poster.
export const CANVAS_WIDTH = 1024;
export const CANVAS_HEIGHT = 1536;
const LINE_WIDTH = 14;
const BACKGROUND_COLOR = '#ffffff';

const fillBackground = (ctx: CanvasRenderingContext2D) => {
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
};

export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  onSave,
  initialDrawing,
  isSaving = false,
  savingMessage
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up canvas: opaque white background so exported PNGs have no alpha.
    fillBackground(ctx);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = LINE_WIDTH;
    ctx.strokeStyle = '#000000';

    setContext(ctx);

    // Load initial drawing if provided
    if (initialDrawing) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      };
      img.src = initialDrawing;
    }
  };

  useEffect(() => {
    initCanvas();
  }, [initialDrawing]);

  // Prevent body scrolling when drawing
  useEffect(() => {
    if (isDrawing) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isDrawing]);

  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX: number;
    let clientY: number;

    if ('touches' in e) {
      // Touch event
      const touch = e.touches[0] || e.changedTouches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling on touch devices
    if (!context || isSaving) return;
    const { x, y } = getCanvasCoordinates(e);

    context.beginPath();
    context.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling on touch devices
    if (!isDrawing || !context || isSaving) return;
    const { x, y } = getCanvasCoordinates(e);

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Clearing is local only; nothing is sent until the drawer confirms a save.
  const clearCanvas = () => {
    if (!context || isSaving) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    fillBackground(context);
  };

  const confirmSave = () => {
    setIsConfirming(false);
    onSave(canvasRef.current?.toDataURL('image/png') ?? '');
  };

  return (
    <>
      <div className="drawing-container" ref={containerRef}>
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
          className={`drawing-canvas${isSaving ? ' locked' : ''}`}
        />
        {isSaving && (
          <div className="drawing-saving-overlay">
            <div className="drawing-saving-spinner" />
            {savingMessage && <p>{savingMessage}</p>}
          </div>
        )}
      </div>
      <div className="drawing-controls">
        <button onClick={clearCanvas} disabled={isSaving} className="clear-button reverse">
          Clear
        </button>
        <button
          className="save-button"
          disabled={isSaving}
          onClick={() => setIsConfirming(true)}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <Modal
        isActive={isConfirming}
        isInstant
        message="Submit this drawing?"
        confirmMessage="Submit"
        cancelMessage="Cancel"
        onConfirm={confirmSave}
        onCancel={() => setIsConfirming(false)}
      />
    </>
  );
};
